
import { ParsedUrlParams, UrlParams } from "./urlparams";
import ClickData, { Result } from "./clickdata";
import SessionStore from "./storesession";
import CookieStore from "./storecookie";
import { GoogleAnalytics } from "./googleanalytics";
import Cacheable from "./cacheable";
import { MapCookie, PardotTypeEnum } from "./mapcookie";
import Referrer from "./referrer";
import { VERSION } from "./version";
import ChannelGrouping from "./channelgrouping";

export enum SessionProfileAttribute {

    // Google Analytics Data
    clientId = 'clientId',
    sessionId = 'sessionId',
    hitId = 'hitId',

    // Source Data
    referrer = 'referrer',
    channelGrouping = 'channelGrouping',

    // UTMs
    utmCampaignId = 'utmCampaignId',
    utmCampaignSource = 'utmCampaignSource',
    utmCampaignMedium = 'utmCampaignMedium',
    utmCampaignName = 'utmCampaignName',
    utmCampaignTerm = 'utmCampaignTerm',
    utmCampaignContent = 'utmCampaignContent',

    // Ads Metadata
    clickId = 'clickId',
    clickPlatform = 'clickPlatform',
    gclid = 'gclid',
    gbraid = 'gbraid',
    wbraid = 'wbraid',
    dclid = 'dclid',
    msclkid = 'msclkid',
    fbclid = 'fbclid',
    kcid = 'kcid',

    // HTTP Client metadata
    userAgent = 'userAgent',
    browserVendor = 'browserVendor',
    browserLanguage = 'browserLanguage',

    // MAP cookies
    activeDemandSessionGuidCookie = 'activeDemandSessionGuidCookie',
    actonWebsiteProspectorCookieKeys = 'actonWebsiteProspectorCookieKeys',
    actonWebsiteProspectorCookieValues = 'actonWebsiteProspectorCookieValues',
    hubspotContactCookie = 'hubspotContactCookie',
    pardotVisitorIdCookieKeys = 'pardotVisitorIdCookieKeys',
    pardotVisitorIdCookieValues = 'pardotVisitorIdCookieValues',
    pardotVisitorHashCookieKeys = 'pardotVisitorHashCookieKeys',
    pardotVisitorHashCookieValues = 'pardotVisitorHashCookieValues',

    // Lead Source data
    leadSources = 'leadSources',
    leadSourceCategories = 'leadSourceCategories',

    // Diagnostic Data
    diagnosticCookiesAllowed = 'diagnosticCookiesAllowed',
    diagnosticSessionStorageAllowed = 'diagnosticSessionStorageAllowed',
    diagnosticVersion = 'diagnosticVersion',
    sessionRestored = 'sessionRestored',

    // All Values
    allValues = 'allValues',

    // Lead Classification Attributes
    leadIsUnpaidOrBrandedPaid = 'leadIsUnpaidOrBrandedPaid',
    leadIsUnpaid = 'leadIsUnpaid',

    // Stored attributes not meant to be queried directly.
    urlParams = 'urlParams',

}

export interface SessionProfileQuery {
    attribute: SessionProfileAttribute;
    index: number;
}


export interface SessionProfile {

    // Google Analytics Data
    [SessionProfileAttribute.clientId]: Array<string>;
    [SessionProfileAttribute.sessionId]: Array<string>;
    [SessionProfileAttribute.hitId]: Array<string>;

    // Source Data
    [SessionProfileAttribute.referrer]: Array<string>;
    [SessionProfileAttribute.channelGrouping]: Array<string>;

    // UTMs
    [SessionProfileAttribute.utmCampaignId]: Array<string>;
    [SessionProfileAttribute.utmCampaignSource]: Array<string>;
    [SessionProfileAttribute.utmCampaignMedium]: Array<string>;
    [SessionProfileAttribute.utmCampaignName]: Array<string>;
    [SessionProfileAttribute.utmCampaignTerm]: Array<string>;
    [SessionProfileAttribute.utmCampaignContent]: Array<string>;

    // Ads Metadata
    [SessionProfileAttribute.clickId] : Array<string>;
    [SessionProfileAttribute.clickPlatform]: Array<string>;
    [SessionProfileAttribute.gclid] : Array<string>;
    [SessionProfileAttribute.gbraid] : Array<string>;
    [SessionProfileAttribute.wbraid] : Array<string>;
    [SessionProfileAttribute.dclid] : Array<string>;
    [SessionProfileAttribute.msclkid] : Array<string>;
    [SessionProfileAttribute.fbclid] : Array<string>;
    [SessionProfileAttribute.kcid] : Array<string>;

    // HTTP Client metadata
    [SessionProfileAttribute.userAgent]: Array<string>;
    [SessionProfileAttribute.browserVendor]: Array<string>;
    [SessionProfileAttribute.browserLanguage]: Array<string>;

    // MAP cookies
    [SessionProfileAttribute.activeDemandSessionGuidCookie]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: Array<string>;
    [SessionProfileAttribute.hubspotContactCookie]: Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieValues] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieValues] : Array<string>;

    // Lead source data
    [SessionProfileAttribute.leadSources]: Array<string>;
    [SessionProfileAttribute.leadSourceCategories]: Array<string>;

    // Diagnostic Data
    [SessionProfileAttribute.diagnosticCookiesAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticSessionStorageAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticVersion]: Array<string>;
    [SessionProfileAttribute.sessionRestored]: Array<boolean>;

    // All Values is not an attribute of session profile
    // but a pointer to the entire profile's contents
    // (special field)

    // Lead Classification Attributes
    [SessionProfileAttribute.leadIsUnpaid]: Array<number>;
    [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: Array<number>;

    // Array of JSON objects with ParsedUrlParams interface.
    [SessionProfileAttribute.urlParams]: Array<string>;
}

export interface SessionProfileQueryBinding {
    element: HTMLInputElement;
    query: SessionProfileQuery;
}

export class Loader
{

    protected cache: Cacheable;
    protected sessionExpiry: number;

    constructor(sessionExpiry: number = 1800000) {
        this.cache = new Cacheable();
        this.sessionExpiry = sessionExpiry;
    }

    public execute() : SessionProfile
    {
        // if session storage is already occupied, then skip the restore
        let restored = false;
        if (!this.hasSessionSet()) {
            restored = this.attemptSessionRestore();
        } else {
            restored = this.previouslyRestored();
        }

        // allow caching the urlparams in the profile
        // so that url param context is not lost when a profile is restored.
        const paramsJson = this.findOrCalculate(SessionProfileAttribute.urlParams, [JSON.stringify(UrlParams.parse())]);
        let params: ParsedUrlParams = JSON.parse(paramsJson[0]);
        // if params are empty, then check again and reset
        if (Object.keys(params).length === 0) {
            params = UrlParams.parse();
            if (Object.keys(params).length > 0) {
                this.cache.store.set(`fountain-analytics-${SessionProfileAttribute.urlParams}`, [JSON.stringify(params)]);
            }
        }

        // always parse click data fresh from the params, which may be cached.
        const clickData = ClickData.parse(params);

        const gclid = this.findOrCalculate(SessionProfileAttribute.gclid, UrlParams.get('gclid', params));
        const gbraid = this.findOrCalculate(SessionProfileAttribute.gbraid, UrlParams.get('gbraid', params));
        const wbraid = this.findOrCalculate(SessionProfileAttribute.wbraid, UrlParams.get('wbraid', params));
        const dclid = this.findOrCalculate(SessionProfileAttribute.dclid, UrlParams.get('dclid', params));
        const msclkid = this.findOrCalculate(SessionProfileAttribute.msclkid, UrlParams.get('msclkid', params));
        const fbclid = this.findOrCalculate(SessionProfileAttribute.fbclid, UrlParams.get('fbclid', params));
        const kcid = this.findOrCalculate(SessionProfileAttribute.kcid, UrlParams.get('kcid', params));

        let originalSource = this.findOrCalculate(SessionProfileAttribute.utmCampaignSource, UrlParams.get('utm_source', params));
        let originalMedium = this.findOrCalculate(SessionProfileAttribute.utmCampaignMedium, UrlParams.get('utm_medium', params));
        let source: Array<string> = [];
        let medium: Array<string> = [];
        // calculate commonly associated source / medium
        // based on click identifier / platform
        if (gclid.length || gbraid.length || wbraid.length) {
            source = ['google'];
            medium = ['cpc'];
        } else if (dclid.length) {
            source = ['google'];
            medium = ['display'];
        } else if (msclkid.length) {
            source = ['bing'];
            medium = ['cpc'];
        } else if (fbclid.length) {
            source = ['meta'];
            medium = ['paid-social'];
        }
        // respect the original source/medium if provided
        if (originalSource.length) {
            source = originalSource;
        }
        if (originalMedium.length) {
            medium = originalMedium;
        }

        // calculate unpaid lead
        const channelGrouping = this.findOrCalculate(SessionProfileAttribute.channelGrouping, [ChannelGrouping.get(params)]);
        let unpaidLead = this.calculateUnpaidLead(channelGrouping);
        
        // calculate unpaid and branded
        const campaignName = this.findOrCalculate(SessionProfileAttribute.utmCampaignName, UrlParams.getOneOf(['utm_campaign', 'campaign'], params));
        let unpaidOrBrandedLead = this.calculateUnpaidOrBrandedPaidLead(campaignName, unpaidLead);

        const profile = {
            // Google Analytics Data
            [SessionProfileAttribute.clientId]: this.findOrCalculate(SessionProfileAttribute.clientId, GoogleAnalytics.getClientIds()),
            [SessionProfileAttribute.sessionId]: this.findOrCalculate(SessionProfileAttribute.sessionId, GoogleAnalytics.getSessionIds()),
            [SessionProfileAttribute.hitId]: GoogleAnalytics.getHitIds(),  // not cached!
        
            // Source Data - Always one of each so wrap in array for API consistency
            [SessionProfileAttribute.referrer]: this.findOrCalculate(SessionProfileAttribute.referrer, Referrer.get()),
            [SessionProfileAttribute.channelGrouping]: channelGrouping,

            // UTMs
            [SessionProfileAttribute.utmCampaignId]: this.findOrCalculate(SessionProfileAttribute.utmCampaignId, UrlParams.get('utm_id', params)),
            [SessionProfileAttribute.utmCampaignSource]: source,
            [SessionProfileAttribute.utmCampaignMedium]: medium,
            [SessionProfileAttribute.utmCampaignName]: campaignName,
            [SessionProfileAttribute.utmCampaignTerm]: this.findOrCalculate(SessionProfileAttribute.utmCampaignTerm, UrlParams.getOneOf(['utm_term','keyword'], params)),
            [SessionProfileAttribute.utmCampaignContent]: this.findOrCalculate(SessionProfileAttribute.utmCampaignContent, UrlParams.get('utm_content', params)),
        
            // Ads Metadata
            [SessionProfileAttribute.clickId] : this.findOrCalculate(SessionProfileAttribute.clickId, clickData.id),
            [SessionProfileAttribute.clickPlatform]: this.findOrCalculate(SessionProfileAttribute.clickPlatform, clickData.platform),
            [SessionProfileAttribute.gclid] : gclid,
            [SessionProfileAttribute.gbraid] : gbraid,
            [SessionProfileAttribute.wbraid] : wbraid,
            [SessionProfileAttribute.dclid] : dclid,
            [SessionProfileAttribute.msclkid] : msclkid,
            [SessionProfileAttribute.fbclid] : fbclid,
            [SessionProfileAttribute.kcid] : kcid,

            // HTTP Client metadata - naturally persistent values so not cached
            [SessionProfileAttribute.userAgent]: [String(window.navigator.userAgent ? window.navigator.userAgent : '')],
            [SessionProfileAttribute.browserVendor]: [String(window.navigator.vendor ? window.navigator.vendor : '')],
            [SessionProfileAttribute.browserLanguage]: [String(window.navigator.language ? window.navigator.language : '')],
        
            // MAP cookies - naturally persistent values so not cached
            [SessionProfileAttribute.activeDemandSessionGuidCookie]: MapCookie.getActiveDemand(this.cache.store.cookie),
            [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: MapCookie.getActOn(this.cache.store.cookie, false),
            [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: MapCookie.getActOn(this.cache.store.cookie, true),
            [SessionProfileAttribute.hubspotContactCookie]: MapCookie.getHubSpot(this.cache.store.cookie),
            [SessionProfileAttribute.pardotVisitorIdCookieKeys] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorIdKey),
            [SessionProfileAttribute.pardotVisitorIdCookieValues] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorIdValue),
            [SessionProfileAttribute.pardotVisitorHashCookieKeys] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorHashKey),
            [SessionProfileAttribute.pardotVisitorHashCookieValues] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorHashValue),
        
            // Lead source data - always calculated fresh so not cached
            [SessionProfileAttribute.leadSources]: [] as Array<string>,
            [SessionProfileAttribute.leadSourceCategories]: [] as Array<string>,

            // Diagnostic Data - naturally persistent values so not cached
            [SessionProfileAttribute.diagnosticCookiesAllowed]: [this.cache.store.cookie.status],
            [SessionProfileAttribute.diagnosticSessionStorageAllowed]: [this.cache.store.session.status],
            [SessionProfileAttribute.diagnosticVersion]: [VERSION],
            // report whether the session was restored from localStorage
            [SessionProfileAttribute.sessionRestored]: [restored],

            // Lead Classification - always calculated fresh
            [SessionProfileAttribute.leadIsUnpaid]: unpaidLead,
            [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: unpaidOrBrandedLead,

            [SessionProfileAttribute.urlParams]: paramsJson,

        };
        if (!restored) {
            this.setRestorePoint(profile);
        } else {
            // only set the session restore flag
            // if we restored sessionStorage from localStorage
            if (typeof(window.sessionStorage) !== 'undefined') {
                window.sessionStorage.setItem('fountain-analytics-restored', '1');
            }
        }
        // the restore point timer for the localStorage profile
        // only expires if the period passes without any web activity
        this.resetRestorePointTimer();
        return profile;
    }

    calculateUnpaidLead(channelGrouping: Array<string>) : Array<number>
    {
        let unpaidLead = [];
        if (channelGrouping.length > 0) {
            for (let i = 0; i < channelGrouping.length; i ++) {
                // evaluate the current channel against the paid channels
                const isPaid = ['Paid Search', 'Display', 'Paid Social'].indexOf(channelGrouping[i]) === -1;
                unpaidLead.push(Number(isPaid));
            }
        }
        return unpaidLead;
    }

    calculateUnpaidOrBrandedPaidLead(campaignName : Array<string>, unpaidLead: Array<number>) : Array<number>
    {
        let unpaidOrBrandedLead = [];
        if (unpaidLead.length > 0) {
            for (let i = 0; i < unpaidLead.length; i ++) {
                let isUnpaidLeadOrBrandedLead = unpaidLead[i];
                if (!isUnpaidLeadOrBrandedLead) {
                    if (campaignName.length > i) {
                        const campaignNameMember = campaignName[i];
                        const isBrandedCampaign = campaignNameMember.toLocaleLowerCase().indexOf('brand') !== -1;
                        if (isBrandedCampaign) {
                            isUnpaidLeadOrBrandedLead = 1;
                        }
                    }
                }
                unpaidOrBrandedLead.push(isUnpaidLeadOrBrandedLead);
            }
        }
        return unpaidOrBrandedLead;
    }

    findOrCalculate(key: string, value: Array<string>) {
        return this.cache.get(`fountain-analytics-${key}`, () => {return value});
    }

    hasSessionSet() : boolean
    {
        const sessionIds = this.cache.get(
            'fountain-analytics-' + SessionProfileAttribute.sessionId, 
            // callback should return empty here
            // to illustrate that there is no session currently set
            () => { return []; }
        );
        return sessionIds.length > 0;
    }

    previouslyRestored() : boolean
    {
        if (typeof(window.sessionStorage) !== 'undefined') {
            return window.sessionStorage.getItem('fountain-analytics-restored') === '1';
        }
        return false;
    }

    /**
     * Attempts to restore a session profile from the last restore point
     * in localStorage (so long as it is valid and not expired)
     * 
     * @returns boolean whether restore was successful or not
     */
    attemptSessionRestore() : boolean
    {
        // ensure browser environment meets all requirements
        // and we have not already restored to the session
        const hasSessionStorage = typeof(window.sessionStorage) !== 'undefined';
        const hasLocalStorage = typeof(window.localStorage) !== 'undefined';
        const isRestored = this.previouslyRestored();
        if (isRestored || !hasLocalStorage || !hasSessionStorage) {
            return false;
        }

        // check for a profile in local storage
        const maybeProfile = window.localStorage.getItem('fountain-analytics-rp');
        const profileTimeString = window.localStorage.getItem('fountain-analytics-rp-time');
        if (profileTimeString === null) {
            return false;
        }
        const profileTime = parseInt(profileTimeString);
        if (Number.isNaN(profileTime)) {
            return false;
        }
        const currentTs = Date.now();
        if (profileTime < currentTs - this.sessionExpiry) {
            // expired - localstorage set more than max time ago
            this.removeRestorePoint();
            return false;
        }
        let profile = null;
        try {
            // profile is actually a SessionProfile
            // serialized to JSON
            // but there is no guarantee of type safety
            // so we have to capture, catch and validate
            profile = JSON.parse(maybeProfile);
        } catch (e) {
            console.error(e);
        }
        if (typeof(profile) === 'object') {
            const keys = Object.keys(profile);
            // distribute the recovered profiles data to the cache
            for (let i = 0; i < keys.length; i ++) {
                const key = keys[i];
                const result = this.cache.store.set(`fountain-analytics-${key}`, profile[key]);
                if (!result) {
                    // storage failure = no session restore
                    return false;
                }
            }
            return true;
        }
        return false;
    }

    removeRestorePoint() : void
    {
        if (typeof(window.localStorage) !== 'undefined') {
            window.localStorage.removeItem('fountain-analytics-rp');
            window.localStorage.removeItem('fountain-analytics-rp-time');
        }
    }

    /**
     * Sets the last restore point from the session
     * 
     * @param profile 
     */
    setRestorePoint(profile: SessionProfile) : void 
    {
        if (typeof(window.localStorage) !== 'undefined') {
            const json = JSON.stringify(profile);
            window.localStorage.setItem('fountain-analytics-rp', json);
        }          
    }

    /**
     * Reset the timer for localStorage restore point
     */
    resetRestorePointTimer() : void
    {
        window.localStorage.setItem('fountain-analytics-rp-time', Date.now().toString());
    }

}
